import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AsamcoUserService } from '@asamco/base';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Company } from '../interfaces/company.interface';
import { functionAppsDetailed } from '../interfaces/functionApps-detailed.interface';
import { functionApps, functionAppsCreateDto } from '../interfaces/functionApps.interface';
import { SelectedCompanyService } from '../selected-company/selected-company.service';
import { FunctionAppsService } from '../services/function-apps.service';
import { LinkTemplateService } from '../services/link-template.service';
import { LinkService } from '../services/link.service';
import { Application } from '../interfaces/application.interface';
import { ApplicationService } from '../services/application.service';
import { LinkTemplate } from '../interfaces/link-template.interface';
import { showErrorSwal } from '@asamco/utils';
import Swal from 'sweetalert2';
import { AsyncPipe, NgIf } from '@angular/common';
import { DetailedGridLinkFunctionappsComponent } from './detailed-grid-link-functionapps/detailed-grid-link-functionapps.component';
import { DxiColumnModule, DxoMasterDetailModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule, DxSelectBoxModule } from 'devextreme-angular';
import { DetailedGridComponent } from './detailed-grid/detailed-grid.component';

@Component({
	selector: 'app-shell-function-apps',
	templateUrl: './function-apps.component.html',
	styleUrls: ['./function-apps.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, AsyncPipe, DetailedGridLinkFunctionappsComponent, DxoMasterDetailModule, DxiColumnModule, DxoSelectionModule, DxDataGridModule, DxSelectBoxModule, DetailedGridComponent]
})
export class FunctionAppsComponent implements OnInit {
	public isLoadingFunctionAppsInfo: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isRefreshingFunctionAppsStatus = new BehaviorSubject<boolean>(false);
	public canManageFunctionAppsSubject = new BehaviorSubject<boolean>(false);
	public linksConfiguration$: Observable<any[]> = new Observable<any[]>();
	public functionApps$ = new BehaviorSubject<functionAppsDetailed[]>([]);
	public companies$ = new BehaviorSubject<Company[]>([]);
	public applications$ = new BehaviorSubject<Application[]>([]);
	public linkTemplates$ = new BehaviorSubject<LinkTemplate[]>([]);

	constructor(
		private asamcoUserService: AsamcoUserService,
		private linkTemplateService: LinkTemplateService,
		private functionAppsService: FunctionAppsService,
		private linkService: LinkService,
		private applicationService: ApplicationService,
		private selectedCompanyService: SelectedCompanyService
	) {}

	ngOnInit(): void {
		this.asamcoUserService.hasAssignedPermission('AL3_FUNC_APP').then(result => {
			this.canManageFunctionAppsSubject.next(result);
			if (!result) return;

			this.getRequiredData();
			this.getFunctionAppItems();
		});
	}

	getRequiredData() {
		forkJoin({
			applications: this.applicationService.get(),
			linkTemplates: this.linkTemplateService.getAllLinkTemplates(),
			companies: this.selectedCompanyService.getCompanies(),
		}).subscribe(({ applications, linkTemplates, companies }) => {
			this.applications$.next(applications);
			this.linkTemplates$.next(linkTemplates);
			this.companies$.next(companies);
		});
	}

	selectCompany(event: any) {
		//console.log(event)
		const companyId = event.value;

		const linkServiceCall = this.linkService.getByCompanyId(companyId);

		// Combine the results using forkJoin and map operator
		this.linksConfiguration$ = linkServiceCall;
	}

	getFunctionAppItems() {
		this.isLoadingFunctionAppsInfo.next(true);
		this.functionAppsService.getFunctionApps().pipe(
			catchError(error => {
				this.isLoadingFunctionAppsInfo.next(false);
				showErrorSwal(error);
				return of([]);
			}),
		).subscribe(configuredApps => {
			const configuredFunctionApps = configuredApps.map(functionApp => this.toBaseFunctionAppDetailed(functionApp));
			this.functionApps$.next(configuredFunctionApps);
			this.isLoadingFunctionAppsInfo.next(false);
			this.refreshFunctionAppStatuses();
		});
	}

	private refreshFunctionAppStatuses(): void {
		this.isRefreshingFunctionAppsStatus.next(true);
		this.functionAppsService.getfunctionAppsDetailed().pipe(
			catchError(error => {
				this.isRefreshingFunctionAppsStatus.next(false);
				showErrorSwal(error);
				return of(this.functionApps$.value.map(functionApp => ({
					...functionApp,
					statusCode: functionApp.statusCode === 'Loading' ? '' : functionApp.statusCode,
					errorMessage: functionApp.errorMessage || 'Unable to refresh function app status.',
				})));
			}),
		).subscribe(detailedFunctionApps => {
			this.functionApps$.next(this.mergeFunctionAppsDetailed(this.functionApps$.value, detailedFunctionApps));
			this.isRefreshingFunctionAppsStatus.next(false);
		});
	}

	private toBaseFunctionAppDetailed(functionApp: functionApps): functionAppsDetailed {
		return {
			functionApps: functionApp,
			applicationLinkEndpointInformation: [],
			errorMessage: '',
			statusCode: 'Loading',
			deployedVersion: '',
			deployedApp: '',
		};
	}

	private mergeFunctionAppsDetailed(currentItems: functionAppsDetailed[], detailedItems: functionAppsDetailed[]): functionAppsDetailed[] {
		const detailedById = new Map(detailedItems.map(item => [item.functionApps.id, item] as const));

		return currentItems.map(currentItem => {
			const detailedItem = detailedById.get(currentItem.functionApps.id);
			if (!detailedItem) {
				return {
					...currentItem,
					statusCode: currentItem.statusCode || 'Unavailable',
					errorMessage: currentItem.errorMessage || 'No function app status information was returned.',
				};
			}

			return {
				...currentItem,
				...detailedItem,
				applicationLinkEndpointInformation: detailedItem.applicationLinkEndpointInformation ?? [],
			};
		});
	}

	rowInserted(data: any) {
		console.log(data);
		//Create new entry
		const newFunctionApp: functionAppsCreateDto = {
			name: data.data.functionApps.name,
			description: data.data.functionApps.description,
			applicationId: data.data.functionApps.applicationId,
			url: data.data.functionApps.url,
			isDefault: data.data.functionApps.isDefault ? data.data.functionApps.isDefault : false,
			masterKey: data.data.functionApps.masterKey,
			systemKey: data.data.functionApps.systemKey,
		};

		this.functionAppsService.createFunctionApps(newFunctionApp).subscribe(
			e => {
				Swal.fire({
					icon: 'success',
					title: 'Link function app configuration saved',
					showConfirmButton: false,
					timer: 1500,
				});
				this.getFunctionAppItems();
			},
			error => {
				showErrorSwal(error);
			}
		);
	}

	rowUpdating(data: any) {
		data.cancel = true;
		console.log(data);

		const updatedEntity = this.updateModel(data.oldData.functionApps, data.newData.functionApps);
		this.functionAppsService.updateFunctionApps(updatedEntity).subscribe(
			e => {
				Swal.fire({
					icon: 'success',
					title: 'Link function app configuration saved',
					showConfirmButton: false,
					timer: 1500,
				});
				this.getFunctionAppItems();
			},
			error => {
				showErrorSwal(error);
			}
		);
	}

	updateModel(oldModel: functionApps, updatedFields: Partial<functionApps>): functionApps {
		// Create a new object to avoid modifying the original oldModel
		const newModel: functionApps = { ...oldModel, ...updatedFields };
		return newModel;
	}

	rowRemoving(data: any) {
		//Cancel event, otherwise the row gets visually deleted anyway...
		data.cancel = true;
		Swal.fire({
			title: `Are you sure you want to delete '${data.data.functionApps.name}'?`,
			text: 'This also deletes all custom function app link configurations with this function app, this cannot be undone!',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes, delete it!',
		}).then(result => {
			if (result.isConfirmed) {
				this.functionAppsService.deleteFunctionApps(data.data.functionApps.id).subscribe(
					e => {
						Swal.fire({
							icon: 'success',
							title: 'Function app entry deleted',
							showConfirmButton: false,
							timer: 1500,
						});
						//reload data'
						this.getRequiredData();
						this.getFunctionAppItems();
					},
					error => {
						showErrorSwal(error);
					}
				);
			}
		});
	}
}
